import { fg } from '@atlaskit/platform-feature-flags';

import { getBscGlobalState, isSSR } from '../../common/utils';

import type { DbAvailabilityResult } from './types';

/**
 * Checks if IndexedDB is available in the current environment
 * @param enableSSRCheck - Whether to check for SSR environment (default: true)
 * @param enableFeatureFlagCheck - Whether to check feature flags and global state (default: true)
 * @returns DbAvailabilityResult with availability status, reason, and bypassImplementation flag
 *
 * When IndexedDB controls are disabled via global state (which includes killswitch checks)
 * or the cpals_indexeddb_support feature gate is disabled, returns available: true with
 * bypassImplementation: true, indicating that the wrapped implementation should be bypassed
 * in favor of direct browser behavior.
 */
export function checkDBAvailability(
	enableSSRCheck: boolean = true,
	enableFeatureFlagCheck: boolean = true,
): DbAvailabilityResult {
	// Check if running in SSR environment
	if (enableSSRCheck && isSSR()) {
		return {
			available: false,
			reason: 'IndexedDB is not available in SSR environment',
		};
	}

	if (!globalThis.indexedDB) {
		return {
			available: false,
			reason: 'IndexedDB is not supported in this browser',
		};
	}

	if (enableFeatureFlagCheck) {
		const globalState = getBscGlobalState();
		if (globalState.indexedDbEnabled === false) {
			return {
				available: true,
				reason: 'Using native IndexedDB: IndexedDB controls are disabled',
				bypassImplementation: true,
			};
		}

		if (!fg('cpals_indexeddb_support')) {
			return {
				available: true,
				reason: 'Using native IndexedDB: IndexedDB support feature gate is disabled',
				bypassImplementation: true,
			};
		}
	}

	return {
		available: true,
	};
}

/**
 * Validates database name format
 * @param dbName - The database name to validate
 * @returns true if valid, false otherwise
 */
export function isValidDBName(dbName: string): boolean {
	// Database name should not be empty and should be a string
	return typeof dbName === 'string' && dbName.trim().length > 0;
}

/**
 * Validates version number
 * @param version - The version number to validate
 * @returns true if valid, false otherwise
 */
export function isValidVersion(version: number | undefined): boolean {
	// Version should be undefined or a positive integer
	if (version === undefined) {
		return true;
	}
	return Number.isInteger(version) && version > 0;
}
